import React, { useEffect, useRef } from "react";

const GRID = 44; // px — the drafting grid the shapes snap to
const INK = (a) => `rgba(158, 197, 232, ${a})`;

const snap = (v) => Math.round(v / GRID) * GRID;
const rand = (min, max) => min + Math.random() * (max - min);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// A field of blueprint shapes: outlined boxes, hatched blocks, tinted
// rectangles and thin text-like bars. Each draws itself in, holds, fades out
// and respawns elsewhere.
function makeShape(W, H) {
  const type = pick([
    "outline",
    "outline",
    "hatch",
    "hatch",
    "fill",
    "fill",
    "bars",
    "square",
  ]);

  const s = {
    type,
    x: snap(rand(-GRID, W - GRID * 2)),
    y: snap(rand(-GRID, H - GRID * 2)),
    w: snap(rand(GRID * 2, GRID * 7)),
    h: snap(rand(GRID, GRID * 4)),
    born: performance.now() + rand(0, 4000),
    drawIn: rand(1800, 3200),
    hold: rand(7000, 16000),
    fade: rand(1500, 2500),
    alpha: rand(0.6, 1), // per-shape intensity variation
  };

  if (type === "bars") {
    s.w = snap(rand(GRID * 3, GRID * 8));
    s.h = snap(rand(GRID, GRID * 2));
    const rows = Math.max(2, Math.round(s.h / (GRID / 2)));
    s.barWidths = Array.from({ length: rows }, (_, i) =>
      s.w * (i === rows - 1 ? 0.6 : rand(0.82, 1))
    );
  }

  if (type === "square") {
    s.w = GRID;
    s.h = GRID;
  }

  return s;
}

// -> [alpha 0..1, draw progress 0..1], or null when the shape should respawn.
function life(s, now) {
  const t = now - s.born;
  if (t < 0) return [0, 0];
  if (t < s.drawIn) {
    const p = t / s.drawIn;
    return [Math.min(1, p * 2), 1 - Math.pow(1 - p, 2)];
  }
  if (t < s.drawIn + s.hold) return [1, 1];
  if (t < s.drawIn + s.hold + s.fade) {
    return [1 - (t - s.drawIn - s.hold) / s.fade, 1];
  }
  return null;
}

function drawShape(ctx, s, a, progress) {
  const { x, y, w, h } = s;
  ctx.globalAlpha = a * s.alpha;

  if (s.type === "outline") {
    // The stroke travels around the perimeter as progress advances.
    const perim = 2 * (w + h);
    ctx.strokeStyle = INK(0.2);
    ctx.lineWidth = 1;
    ctx.setLineDash([perim * progress, perim]);
    ctx.strokeRect(x + 0.5, y + 0.5, w, h);
    ctx.setLineDash([]);
  } else if (s.type === "fill") {
    ctx.fillStyle = INK(0.055);
    ctx.fillRect(x, y, w, h * progress);
  } else if (s.type === "hatch") {
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.strokeStyle = INK(0.11);
    ctx.lineWidth = 1;
    ctx.beginPath();
    const span = (w + h) * progress;
    for (let d = 0; d < span; d += 7) {
      ctx.moveTo(x + d, y);
      ctx.lineTo(x, y + d);
    }
    ctx.stroke();
    ctx.restore();
  } else if (s.type === "bars") {
    // Stacked thin bars, like redacted spec text.
    ctx.fillStyle = INK(0.13);
    const visible = Math.ceil(s.barWidths.length * progress);
    for (let i = 0; i < visible; i++) {
      ctx.fillRect(x, y + i * (GRID / 2), s.barWidths[i], 6);
    }
  } else if (s.type === "square") {
    ctx.fillStyle = INK(0.16);
    ctx.fillRect(x, y, w * progress, h);
  }
}

// Fixed, full-viewport animated backdrop. Purely decorative: it sits behind
// every page at z-0, so page content needs `relative` (or z-10) to stack above.
export default function BlueprintBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let W = 0;
    let H = 0;
    let shapes = [];
    let rafId = null;
    let resizeTimer = null;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = canvas.clientWidth;
      H = canvas.clientHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.max(14, Math.round((W * H) / 52000));
      shapes = Array.from({ length: count }, () => makeShape(W, H));
    };

    const frame = (now) => {
      ctx.clearRect(0, 0, W, H);
      for (let i = 0; i < shapes.length; i++) {
        const state = life(shapes[i], now);
        if (state === null) {
          shapes[i] = makeShape(W, H);
          continue;
        }
        drawShape(ctx, shapes[i], state[0], state[1]);
      }
      ctx.globalAlpha = 1;
      rafId = requestAnimationFrame(frame);
    };

    // Reduced motion: draw the field once, fully rendered, and leave it there.
    const drawStatic = () => {
      ctx.clearRect(0, 0, W, H);
      const now = performance.now();
      shapes.forEach((s) => {
        s.born = now - s.drawIn;
        drawShape(ctx, s, 1, 1);
      });
      ctx.globalAlpha = 1;
    };

    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        resize();
        if (reduced) drawStatic();
      }, 150);
    };

    resize();
    if (reduced) drawStatic();
    else rafId = requestAnimationFrame(frame);

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(resizeTimer);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0 isolate overflow-hidden bg-[radial-gradient(120%_90%_at_50%_-10%,#16202e_0%,#101825_45%,#090e16_100%)]"
    >
      {/* Layer 1: slowly drifting glows */}
      <div className="absolute -left-[12vw] -top-[18vw] h-[55vw] w-[55vw] rounded-full bg-[radial-gradient(circle,rgba(64,110,165,0.20)_0%,transparent_65%)] blur-[90px] will-change-transform motion-safe:animate-bp-drift-a" />
      <div className="absolute -bottom-[16vw] -right-[14vw] h-[48vw] w-[48vw] rounded-full bg-[radial-gradient(circle,rgba(90,140,190,0.13)_0%,transparent_65%)] blur-[90px] will-change-transform motion-safe:animate-bp-drift-b" />

      {/* Layer 2: panning drafting grid — fine 44px rules over 220px majors */}
      <div
        className="absolute -inset-[220px] will-change-transform motion-safe:animate-bp-grid-pan"
        style={{
          backgroundImage: [
            `linear-gradient(${INK(0.03)} 1px, transparent 1px)`,
            `linear-gradient(90deg, ${INK(0.03)} 1px, transparent 1px)`,
            `linear-gradient(${INK(0.055)} 1px, transparent 1px)`,
            `linear-gradient(90deg, ${INK(0.055)} 1px, transparent 1px)`,
          ].join(", "),
          backgroundSize: "44px 44px, 44px 44px, 220px 220px, 220px 220px",
          maskImage:
            "radial-gradient(115% 100% at 50% 40%, black 30%, transparent 78%)",
          WebkitMaskImage:
            "radial-gradient(115% 100% at 50% 40%, black 30%, transparent 78%)",
        }}
      />

      {/* Layer 3: the blueprint shape field */}
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
    </div>
  );
}
